import { TeamAge, TeamGender, TeamType } from '@/constants';
import { AbstractModel } from '@/models/abstractModel';
import { CountryDictionaryItem } from '@/models/dictionary/country';
import { teamService } from '@/services/teamService';
import { useCountryDictionary, teamTypeDictionary, teamGenderDictionary, teamAgeDictionary } from '@/stores';
import type { ITeamApi } from '@/types';
import { useTeamStore } from '@/stores/team';

export class TeamItem extends AbstractModel {
    public countryId: number;
    public ctId: number;
    public gameCountDball: number;
    public gameCountLive: number;
    public iso3: string;
    public logo: string | null;
    public name: string;
    public typeId: TeamType;
    public wikiLink: string | null;
    public women: TeamGender;
    public youth: TeamAge;

    constructor(data: ITeamApi) {
        super();
        this.countryId = data.country_id;
        this.ctId = data.ctId;
        this.gameCountDball = data.game_count_dball;
        this.gameCountLive = data.game_count_live;
        this.iso3 = data.iso3;
        this.logo = data.logo;
        this.name = data.name;
        this.typeId = data.type_id;
        this.wikiLink = data.wiki_link;
        this.women = data.women;
        this.youth = data.youth;
    }

    get genderName(): string {
        return this._lazyLoad('genderName', this.women, () => {
            return teamGenderDictionary.nameById(this.women);
        });
    }

    get typeName(): string {
        return this._lazyLoad('typeName', this.typeId, () => {
            return teamTypeDictionary.nameById(this.typeId);
        });
    }

    get ageName(): string {
        return this._lazyLoad('ageName', this.youth, () => {
            return teamAgeDictionary.nameById(this.youth);
        });
    }

    get logoUrl(): string {
        return this._lazyLoad('logoUrl', this.logo, () => `/teams/${this.logo}`);
    }

    get countryName(): string {
        return this._lazyLoad('countryName', this.countryId, () => {
            const country = this.getCountry();
            if (country !== null) {
                return country.name;
            }

            return '';
        });
    }

    public getCountry(): CountryDictionaryItem {
        const dictionary = useCountryDictionary();
        return dictionary.itemById(this.countryId);
    }

    public isProcessing(): boolean {
        const store = useTeamStore();
        return this.ctId in store.favoriteTeamProcessing;
    }

    public isFavorite(): boolean {
        const store = useTeamStore();
        return this.ctId in store.favoriteTeam;
    }

    public toggleFavorite(): void {
        if (this.isProcessing()) {
            return;
        }

        if (this.isFavorite()) {
            void teamService.removeFavorite(this.ctId);
        } else {
            void teamService.addFavorite(this.ctId);
        }
    }
}
